<template>
  <div class="profile">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ t('profile.title') }}</h1>
        <p class="page-sub">{{ t('profile.sub') }}</p>
      </div>

      <!-- Live preview — same avatar/color the rest of the app will show. -->
      <div class="preview">
        <UserAvatar :user="previewUser" :size="56" />
        <div class="preview-info">
          <div class="preview-name">{{ name || '—' }}</div>
          <div class="preview-role">{{ roleLabel }}</div>
        </div>
      </div>
    </div>

    <!-- Identity ------------------------------------------------ -->
    <section class="panel">
      <header class="panel-head">
        <h2>{{ t('profile.identity') }}</h2>
        <p>{{ t('profile.identitySub') }}</p>
      </header>
      <div class="panel-body two-col">
        <div class="field">
          <label>{{ t('profile.fieldName') }}</label>
          <input v-model="name" type="text" class="form-control" maxlength="120" />
        </div>
        <div class="field">
          <label>{{ t('profile.fieldEmail') }}</label>
          <input :value="user?.email" type="email" class="form-control" disabled />
        </div>
      </div>
      <footer class="panel-foot">
        <span v-if="identitySaved" class="saved-hint">{{ t('profile.saved') }}</span>
        <button class="btn btn-primary" :disabled="savingIdentity || !dirtyIdentity" @click="saveIdentity">
          {{ savingIdentity ? t('common.saving') : t('common.saveChanges') }}
        </button>
      </footer>
    </section>

    <!-- Appearance ---------------------------------------------- -->
    <section class="panel">
      <header class="panel-head">
        <h2>{{ t('profile.appearance') }}</h2>
        <p>{{ t('profile.appearanceSub') }}</p>
      </header>

      <div class="panel-body appearance">
        <!-- Photo -->
        <div class="photo-block">
          <UserAvatar :user="previewUser" :size="96" />
          <div class="photo-actions">
            <button class="btn btn-primary" :disabled="uploadingPhoto" @click="pickFile">
              {{ user?.avatar_url ? t('profile.changePhoto') : t('profile.uploadPhoto') }}
            </button>
            <button
              v-if="user?.avatar_url"
              class="btn btn-ghost"
              :disabled="uploadingPhoto"
              @click="removePhoto"
            >{{ t('profile.removePhoto') }}</button>
            <input
              ref="fileInput"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              class="file-hidden"
              @change="onFile"
            />
            <p class="hint">{{ t('profile.photoHint') }}</p>
          </div>
        </div>

        <!-- Color -->
        <div class="color-block">
          <label class="block-label">{{ t('profile.pickColor') }}</label>
          <div class="swatches" role="radiogroup">
            <button
              v-for="c in PRESETS"
              :key="c"
              class="swatch"
              :class="{ active: color === c }"
              :style="{ background: c }"
              :aria-label="c"
              role="radio"
              :aria-checked="color === c"
              @click="setColor(c)"
            ><span v-if="color === c" class="tick">✓</span></button>
          </div>
          <label class="block-label mt">{{ t('profile.colorCustom') }}</label>
          <div class="custom-color">
            <input type="color" :value="color || '#59CE7F'" @input="setColor($event.target.value)" />
            <input type="text" class="form-control" :value="color || ''" placeholder="#59CE7F" @change="onHexInput" />
          </div>
        </div>
      </div>

      <footer class="panel-foot">
        <span v-if="appearanceSaved" class="saved-hint">{{ t('profile.saved') }}</span>
        <button class="btn btn-primary" :disabled="savingAppearance || !dirtyAppearance" @click="saveAppearance">
          {{ savingAppearance ? t('common.saving') : t('common.saveChanges') }}
        </button>
      </footer>
    </section>

    <!-- Security ------------------------------------------------ -->
    <section class="panel">
      <header class="panel-head">
        <h2>{{ t('profile.security') }}</h2>
        <p>{{ t('profile.securitySub') }}</p>
      </header>
      <form class="panel-body pwd" autocomplete="off" @submit.prevent="changePassword">
        <div class="field">
          <label>{{ t('profile.currentPassword') }}</label>
          <input v-model="pwdCur" type="password" class="form-control" autocomplete="current-password" required />
        </div>
        <div class="field">
          <label>{{ t('profile.newPassword') }}</label>
          <input v-model="pwdNew" type="password" class="form-control" autocomplete="new-password" required minlength="8" />
        </div>
        <div class="field">
          <label>{{ t('profile.confirmPassword') }}</label>
          <input v-model="pwdConfirm" type="password" class="form-control" autocomplete="new-password" required minlength="8" />
        </div>
        <p v-if="pwdError" class="err-hint">{{ pwdError }}</p>
        <footer class="panel-foot inline">
          <span v-if="pwdSaved" class="saved-hint">{{ t('profile.passwordChanged') }}</span>
          <button class="btn btn-primary" type="submit" :disabled="savingPwd">
            {{ savingPwd ? t('common.saving') : t('profile.changePassword') }}
          </button>
        </footer>
      </form>
    </section>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue'
import api from '@/api'
import UserAvatar from '@/components/UserAvatar.vue'
import { useAuth } from '@/composables/useAuth'
import { useI18n } from '@/composables/useI18n'

// Curated palette — soft, on-brand tints only. The custom picker gives full freedom.
const PRESETS = [
  '#59CE7F', '#3EB9A6', '#7EA6F0', '#9F84E8', '#F27D9A',
  '#F5A25D', '#E6B855', '#B591D6', '#6B93D6', '#D66B6B',
]

export default {
  name: 'ProfileView',
  components: { UserAvatar },
  setup() {
    const { t } = useI18n()
    const { currentUser, setUser, isManager, isSuperadmin } = useAuth()

    const user = computed(() => currentUser() || {})
    const name  = ref(user.value.name || '')
    const color = ref(user.value.avatar_color || '')

    // Reflect out-of-band user updates (e.g. /auth/me refreshing on mount).
    watch(() => user.value, (u) => {
      name.value  = u.name || name.value
      if (u.avatar_color !== undefined) color.value = u.avatar_color || ''
    }, { deep: true })

    const previewUser = computed(() => ({
      id:            user.value.id,
      name:          name.value || user.value.name,
      avatar_url:    user.value.avatar_url,
      avatar_color:  color.value,
    }))

    const roleLabel = computed(() => {
      if (isSuperadmin()) return t('roles.manager')
      return isManager() ? t('roles.manager') : t('roles.developer')
    })

    // ── Identity save ──
    const savingIdentity = ref(false)
    const identitySaved  = ref(false)
    const dirtyIdentity  = computed(() => (name.value || '') !== (user.value.name || ''))
    async function saveIdentity() {
      if (!dirtyIdentity.value) return
      savingIdentity.value = true
      identitySaved.value  = false
      try {
        const res = await api.updateProfile({ name: name.value.trim() })
        if (res?.data) setUser(res.data)
        identitySaved.value = true
        setTimeout(() => (identitySaved.value = false), 1800)
      } catch (e) { console.error(e) }
      finally { savingIdentity.value = false }
    }

    // ── Appearance (color + photo) ──
    const savingAppearance = ref(false)
    const appearanceSaved  = ref(false)
    const uploadingPhoto   = ref(false)
    const fileInput = ref(null)
    const dirtyAppearance = computed(() => (color.value || '') !== (user.value.avatar_color || ''))

    function setColor(v) { color.value = (v || '').toLowerCase() }
    function onHexInput(e) {
      const v = (e.target.value || '').trim().toLowerCase()
      if (/^#[0-9a-f]{6}$/.test(v) || v === '') setColor(v)
    }
    async function saveAppearance() {
      if (!dirtyAppearance.value) return
      savingAppearance.value = true
      appearanceSaved.value  = false
      try {
        const res = await api.updateProfile({ avatar_color: color.value || null })
        if (res?.data) setUser(res.data)
        appearanceSaved.value = true
        setTimeout(() => (appearanceSaved.value = false), 1800)
      } catch (e) { console.error(e) }
      finally { savingAppearance.value = false }
    }

    function pickFile() { fileInput.value?.click() }
    async function onFile(e) {
      const f = e.target.files?.[0]
      if (!f) return
      uploadingPhoto.value = true
      try {
        const res = await api.uploadAvatar(f)
        if (res) setUser({ ...user.value, ...res })
      } catch (err) { console.error(err); alert(err.message || 'Upload failed') }
      finally {
        uploadingPhoto.value = false
        if (fileInput.value) fileInput.value.value = ''
      }
    }
    async function removePhoto() {
      uploadingPhoto.value = true
      try {
        const res = await api.deleteAvatar()
        if (res?.data) setUser(res.data)
      } catch (e) { console.error(e) }
      finally { uploadingPhoto.value = false }
    }

    // ── Password ──
    const pwdCur = ref(''), pwdNew = ref(''), pwdConfirm = ref('')
    const pwdError = ref(''), pwdSaved = ref(false), savingPwd = ref(false)
    async function changePassword() {
      pwdError.value = ''
      if (pwdNew.value.length < 8)             { pwdError.value = t('profile.minLen');   return }
      if (pwdNew.value !== pwdConfirm.value)   { pwdError.value = t('profile.mismatch'); return }
      savingPwd.value = true
      try {
        await api.changePassword({ current_password: pwdCur.value, new_password: pwdNew.value })
        pwdCur.value = pwdNew.value = pwdConfirm.value = ''
        pwdSaved.value = true
        setTimeout(() => (pwdSaved.value = false), 2200)
      } catch (e) { pwdError.value = e.message || 'Failed' }
      finally { savingPwd.value = false }
    }

    return {
      t, PRESETS, user, previewUser, roleLabel,
      name, color, setColor, onHexInput,
      savingIdentity, identitySaved, dirtyIdentity, saveIdentity,
      savingAppearance, appearanceSaved, dirtyAppearance, saveAppearance,
      uploadingPhoto, fileInput, pickFile, onFile, removePhoto,
      pwdCur, pwdNew, pwdConfirm, pwdError, pwdSaved, savingPwd, changePassword,
    }
  },
}
</script>

<style scoped>
.profile { display: flex; flex-direction: column; gap: 20px; }

.page-header {
  display: flex; align-items: center; justify-content: space-between; gap: 20px;
  flex-wrap: wrap;
}
.page-title { font-size: 24px; font-weight: 800; letter-spacing: -.02em; line-height: 1.2; }
.page-sub   { color: var(--ink-2); font-size: 14px; margin-top: 4px; }

.preview {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 14px 10px 12px;
  background: var(--surface); border-radius: 16px;
  box-shadow: 0 2px 6px rgba(16,22,40,.04);
}
.preview-name { font-weight: 700; font-size: 15px; color: var(--ink); }
.preview-role { font-size: 12px; color: var(--ink-2); margin-top: 2px; }

.panel {
  background: var(--surface);
  border-radius: 16px;
  padding: 22px;
  box-shadow: 0 2px 8px rgba(16,22,40,.04);
}
.panel-head h2 { font-size: 17px; font-weight: 700; color: var(--ink); }
.panel-head p  { font-size: 13px; color: var(--ink-2); margin-top: 4px; }
.panel-body   { margin-top: 18px; }
.panel-body.two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
@media (max-width: 720px) { .panel-body.two-col { grid-template-columns: 1fr; } }

.field { display: flex; flex-direction: column; gap: 6px; }
.field label { font-size: 13px; font-weight: 600; color: var(--ink-2); }

.panel-foot {
  margin-top: 18px; padding-top: 14px;
  border-top: 1px solid var(--surface-3);
  display: flex; align-items: center; justify-content: flex-end; gap: 12px;
}
.panel-foot.inline { border-top: none; padding-top: 0; margin-top: 4px; }
.saved-hint { color: var(--accent, #2E9E5B); font-size: 12.5px; font-weight: 600; }
.err-hint   { color: #B8432B; font-size: 12.5px; margin-top: 4px; }

/* Appearance layout */
.appearance {
  display: grid; grid-template-columns: 220px 1fr; gap: 28px;
  align-items: start;
}
@media (max-width: 720px) { .appearance { grid-template-columns: 1fr; } }

.photo-block { display: flex; flex-direction: column; align-items: center; gap: 12px; }
.photo-actions { display: flex; flex-direction: column; align-items: center; gap: 8px; }
.file-hidden { display: none; }
.hint { font-size: 11.5px; color: var(--ink-2); text-align: center; }

.block-label { font-size: 13px; font-weight: 600; color: var(--ink-2); display: block; }
.block-label.mt { margin-top: 14px; }

.swatches {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
  gap: 8px; margin-top: 8px;
}
.swatch {
  height: 40px; border-radius: 12px; border: none; cursor: pointer;
  position: relative; transition: transform .12s ease;
  box-shadow: 0 1px 2px rgba(16,22,40,.08);
}
.swatch:hover { transform: translateY(-1px); }
.swatch.active { box-shadow: 0 0 0 3px var(--surface), 0 0 0 5px var(--ink); }
.tick { color: #fff; font-weight: 800; text-shadow: 0 1px 2px rgba(0,0,0,.35); }

.custom-color { display: flex; align-items: center; gap: 10px; margin-top: 8px; }
.custom-color input[type=color] {
  width: 44px; height: 44px; padding: 0; border: none; border-radius: 12px;
  background: none; cursor: pointer;
}
.custom-color .form-control { max-width: 160px; text-transform: lowercase; font-family: var(--font-mono, monospace); }

/* Password form */
.pwd { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
@media (max-width: 720px) { .pwd { grid-template-columns: 1fr; } }
.pwd .err-hint { grid-column: 1 / -1; }
.pwd .panel-foot { grid-column: 1 / -1; }
</style>
